/**
 * Diary Quiz Set API
 */
import { apiClient } from "./client";
import type { ApiResponse } from "@/types/api";
import type {
    DiaryQuizSetResponse,
    DiaryQuizSetSummaryResponse,
    DiaryQuizSetRequest,
} from "@/types/diaryQuizSet";

/**
 * 세트 목록 조회
 */
export const getDiaryQuizSets = async (): Promise<
    DiaryQuizSetSummaryResponse[]
> => {
    const response =
        await apiClient.get<ApiResponse<DiaryQuizSetSummaryResponse[]>>(
            "/diary-quiz-sets",
        );
    return response.data.data!;
};

/**
 * quizId를 JSON에서 항상 문자열로 파싱 (JS Number 정밀도 손실 방지).
 * 응답 텍스트에서 "quizId": number 를 "quizId": "number" 로 치환 후 파싱.
 */
function parseDiaryQuizSetResponseText(text: string): DiaryQuizSetResponse {
    const normalized = text.replace(/"quizId"\s*:\s*(\d+)/g, '"quizId":"$1"');
    const parsed = JSON.parse(normalized) as ApiResponse<DiaryQuizSetResponse>;
    if (!parsed.data) throw new Error("Invalid response");
    const data = parsed.data;
    return {
        ...data,
        items: (data.items ?? []).map((item) => ({
            ...item,
            quizId:
                typeof item.quizId === "string"
                    ? item.quizId
                    : String(item.quizId),
        })),
    };
}

/**
 * 세트 상세 조회 (items 포함)
 * quizId는 JS 정밀도(2^53) 초과 방지를 위해 응답을 text로 받아 string으로만 파싱.
 */
export const getDiaryQuizSet = async (
    setId: number,
): Promise<DiaryQuizSetResponse> => {
    const response = await apiClient.get<string>(`/diary-quiz-sets/${setId}`, {
        responseType: "text",
    });
    return parseDiaryQuizSetResponseText(response.data);
};

/**
 * 세트 생성
 */
export const createDiaryQuizSet = async (
    request: DiaryQuizSetRequest,
): Promise<DiaryQuizSetResponse> => {
    const response = await apiClient.post<ApiResponse<DiaryQuizSetResponse>>(
        "/diary-quiz-sets",
        request,
    );
    return response.data.data!;
};

/**
 * 세트 수정
 */
export const updateDiaryQuizSet = async (
    setId: number,
    request: DiaryQuizSetRequest,
): Promise<DiaryQuizSetResponse> => {
    const response = await apiClient.put<ApiResponse<DiaryQuizSetResponse>>(
        `/diary-quiz-sets/${setId}`,
        request,
    );
    return response.data.data!;
};

/**
 * 세트 삭제
 */
export const deleteDiaryQuizSet = async (setId: number): Promise<void> => {
    await apiClient.delete(`/diary-quiz-sets/${setId}`);
};

/**
 * 세트 순서 변경
 */
export const reorderDiaryQuizSets = async (setIds: number[]): Promise<void> => {
    await apiClient.put("/diary-quiz-sets/reorder", { setIds });
};
